import React from "react";
import { Alert, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { groupLabel } from "../helpers/evalComparisonApi";

const GROUP_COLORS = {
  brick: "error",
  false_positive: "warning",
  true_positive: "success",
  clarification: "info",
  no_match_expected: "default",
};

// Core returns a score of 9000 or more for an exact-text match.
const formatScore = (v) => {
  if (v == null) return null;
  if (v >= 9000) return "exact";
  return v.toFixed(4);
};

const EXPECTED_TEXT = { no_match: "No match expected", unlabeled: "No label" };

export const VersionCell = ({ cell }) => {
  if (!cell) return null;
  const label = groupLabel(cell.group) || (cell.outcome === "error" ? "Error" : "No group");
  const score = formatScore(cell.score);
  return (
    <Stack spacing={0.5} sx={{ py: 1, whiteSpace: "normal" }}>
      <Typography variant="body2">
        {cell.answer || (cell.outcome === "brick" ? "No answer" : cell.outcome === "error" ? "Error" : "—")}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <Tooltip title={cell.group ? "" : "Unlabeled and error rows appear only under All"}>
          <Chip size="small" label={label} color={GROUP_COLORS[cell.group] || "default"} variant="outlined" />
        </Tooltip>
        {score && (
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
            {score}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

// Sort fields the API accepts, keyed by column field.
export const SORT_FIELD = {
  script: "script",
  line_order: "line_order",
  question: "question",
  expected_bucket: "accepted",
};

// One row per golden set question, one column per compared engine version.
// The server pages and sorts; the page passes the view's sort as sortModel.
const ComparisonTable = ({
  rows,
  versions,
  total,
  loading,
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
}) => {
  const columns = [
    { field: "script", headerName: "Script", width: 170 },
    {
      field: "line_order",
      headerName: "Line",
      width: 70,
      type: "number",
      valueFormatter: (v) => (v == null ? "—" : v),
    },
    { field: "question", headerName: "Question", flex: 2, minWidth: 220 },
    {
      field: "expected_bucket",
      headerName: "Golden set (expected)",
      flex: 1,
      minWidth: 170,
      valueGetter: (v, row) => v || EXPECTED_TEXT[row.expected] || "—",
    },
    ...versions.map((v) => ({
      field: `version_${v.key}`,
      headerName: v.label,
      flex: 1.5,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => <VersionCell cell={params.row.versions[v.key]} />,
    })),
  ];

  const noDialogueOrder = rows.length > 0 && rows.every((r) => r.line_order == null);

  return (
    <Stack spacing={1}>
      {noDialogueOrder && (
        <Alert severity="info">This run has no dialogue order. Rows are sorted by script and question.</Alert>
      )}
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        autoHeight
        getRowHeight={() => "auto"}
        paginationMode="server"
        sortingMode="server"
        rowCount={total}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        localeText={{ noRowsLabel: "No questions match these filters" }}
        sx={{ border: 0 }}
      />
    </Stack>
  );
};

export default ComparisonTable;
